// src/hooks/useANPREvent.ts

import { ANPREvent } from 'domain/anpr-event'
import { useLoading } from 'layout/context/loading-context'
import { useCallback, useState } from 'react'
import anprEventApiService from '../api/anpr-event.api'
import { ANPREventQueryParams } from '../types/anpr-event-query-param'
import { ANPREventCountResponse } from '../types/anpr-event-count-response'

interface UseANPREventHookReturn {
  anprEvents: ANPREvent[]
  totalEvents: number
  error: Error | null
  fetchANPREvents: (params?: ANPREventQueryParams) => Promise<void>
  fetchANPREventsCount: (
    params?: ANPREventQueryParams
  ) => Promise<ANPREventCountResponse | undefined>
}

export const useANPREvent = (): UseANPREventHookReturn => {
  const { startLoading, stopLoading } = useLoading()

  const [anprEvents, setANPREvents] = useState<ANPREvent[]>([])
  const [totalEvents, setTotalEvents] = useState<number>(0)
  const [error, setError] = useState<Error | null>(null)

  const fetchANPREvents = useCallback(
    async (params?: ANPREventQueryParams): Promise<void> => {
      if (!params?.clientId) {
        return
      }

      startLoading('fetchANPREvents')
      setError(null)

      try {
        const response = await anprEventApiService.getANPREvents(params)

        setANPREvents(response.responseObject)
        setTotalEvents(response.responseObject.length)
      } catch (err) {
        setError(err as Error)
        setANPREvents([])
      } finally {
        stopLoading('fetchANPREvents')
      }
    },
    []
  )

  const fetchANPREventsCount = useCallback(
    async (
      params?: ANPREventQueryParams
    ): Promise<ANPREventCountResponse | undefined> => {
      if (!params?.clientId) {
        return
      }
      startLoading('fetchANPREventsCount')
      setError(null)

      try {
        const response = await anprEventApiService.getANPREventsCountByDate(
          params
        )
        return response.responseObject
      } catch (err) {
        setError(err as Error)
      } finally {
        stopLoading('fetchANPREventsCount')
      }
    },
    []
  )

  return {
    anprEvents,
    totalEvents,
    error,
    fetchANPREvents,
    fetchANPREventsCount,
  }
}
